/**
 * Isolated previews of a pull request.
 *
 * F3 tries a pull request *in place*: the shared `qa` worktree wears it, one at
 * a time, and the PM's data is put at risk by whatever migration the branch
 * brings. That is enough for "does this look right?" and useless for "these two
 * pull requests both touch the schema, review them this afternoon" — which is
 * the ordinary case.
 *
 * A preview is the answer: its own worktree branched from the environment's
 * commit with `refs/pull/<n>/head` merged on top, its own sandbox databases
 * restored from a copy of the environment's, its own copy of the inherited
 * configuration files, and its own hostname. Nothing it does reaches `qa`, so
 * three of them can exist at once and be thrown away without a restore.
 *
 * Two properties shape the model below:
 *
 * - **It is derived, never authored.** Every field is something the server
 *   resolved (a commit, a port, a hostname) or decided (an expiry). Nothing here
 *   comes from a model or a form, which is why there is no name, no title and no
 *   description: a preview is identified by the pull request it shows.
 * - **It is temporary on purpose.** `expiresAt` is not a soft hint — a sweep
 *   destroys what it names — because the disk cost of a preview is a worktree, a
 *   node_modules and a database, and the failure mode of forgetting is an EC2
 *   that fills up quietly. Extending is a deliberate act somebody performs.
 *
 * @module prPreviews
 */
import { Schema } from "effect";

import {
  EnvironmentId,
  IsoDateTime,
  NonNegativeInt,
  PositiveInt,
  PrPreviewId,
  ProjectId,
  TrimmedNonEmptyString,
  UserId,
} from "./baseSchemas";

/**
 * Lifecycle of a preview.
 *
 * `conflict` is a first-class state rather than a flavour of `failed` because
 * it is the one outcome that is nobody's fault and is *actionable*: the pull
 * request does not merge onto the environment's commit, the agent has to say
 * which files clash, and the answer changes as soon as the author rebases —
 * `refresh` moves the same row from `conflict` to `ready` with no new identity.
 * Merged into `failed` it would read as "the server broke" and invite a retry
 * that cannot possibly work.
 *
 * A `conflict` holds no worktree, no database and no dev server: the merge is
 * aborted and the half-built workspace removed, so what remains is the record
 * of the attempt and the list of files. `failed` is the opposite — something
 * went wrong mid-build and there may be resources to clean up — which is why
 * the two are not one state with a reason string.
 */
export const PrPreviewStatus = Schema.Literals([
  "provisioning",
  "ready",
  "conflict",
  "failed",
  "destroyed",
]);
export type PrPreviewStatus = typeof PrPreviewStatus.Type;

/**
 * Where the preview's rows came from.
 *
 * `snapshot:<id>` when they were restored from a copy of the base environment's
 * databases — the normal case, and the reason a preview is worth looking at at
 * all: it holds the data somebody spent a week getting right. `template` is
 * reserved for the PostgreSQL `CREATE DATABASE … TEMPLATE` shortcut, which is
 * deliberately out of scope here. `none` is what a project with no sandbox
 * database gets, and is not an error.
 *
 * Free text rather than a union of literals because the snapshot id travels
 * inside it and a union would have to be parsed to be useful; readers that care
 * about the id already know the prefix.
 */
export const PR_PREVIEW_DATA_SOURCE_NONE = "none";
export const prPreviewSnapshotDataSource = (snapshotId: string): string => `snapshot:${snapshotId}`;

/**
 * The ceiling on live previews and their lifetime are *not* declared here.
 *
 * They live in `@taskin-code/shared/projectConfig` as
 * `DEFAULT_QA_PREVIEW_MAX_ALIVE` / `DEFAULT_QA_PREVIEW_TTL_HOURS`, next to the
 * `.taskin/config.json → qa.preview` block that overrides them. Two constants
 * for one number is how a project override starts disagreeing with the default
 * nobody remembered to change.
 */

/**
 * The branch a preview's worktree lives on.
 *
 * Outside the naming scheme of temporary thread branches, like an environment's
 * `taskin/env-<name>`, so that no automatic rename can rewrite it, and derived
 * from the pull request number so that a stray worktree found on disk at 3am
 * says what it is.
 */
export const prPreviewBranchName = (prNumber: number): string => `taskin/preview-pr-${prNumber}`;

/**
 * The address a preview serves on, from the row rather than guessed.
 *
 * Here rather than in each reader because there are four of them — the HTTP
 * route, the agent's tools, the GitHub automation's comments and the card — and
 * a URL assembled four times is a URL that will eventually differ between the
 * one quoted in a review and the one that answers. Null while no hostname has
 * been allocated, which is every preview that is still provisioning and every
 * one that ended in a conflict.
 */
export const prPreviewUrl = (preview: { readonly devHostname: string | null }): string | null =>
  preview.devHostname === null ? null : `https://${preview.devHostname}`;

export const PrPreview = Schema.Struct({
  id: PrPreviewId,
  projectId: ProjectId,
  /**
   * The project environment this preview was branched and copied from.
   *
   * Recorded rather than resolved on demand: the preview keeps showing the code
   * and data of the environment *as it stood when it was created*, and the
   * environment moves on. It is also what "refresh" re-reads, and what the
   * environment card groups its list of previews by.
   */
  baseEnvironmentId: EnvironmentId,
  prNumber: PositiveInt,
  /**
   * Commit of `refs/pull/<n>/head` that was merged in, or null while nothing
   * has been fetched yet. Null in `provisioning`, set from the first fetch
   * onwards — including on a `conflict`, where it is the commit that did not
   * merge and therefore the thing a refresh compares against.
   */
  headSha: Schema.NullOr(TrimmedNonEmptyString),
  /** Commit of the base environment's worktree the preview was branched from. */
  baseSha: TrimmedNonEmptyString,
  status: PrPreviewStatus,
  branch: TrimmedNonEmptyString,
  /** Absolute path of the worktree. Removed on destroy; the row keeps the path. */
  worktreePath: TrimmedNonEmptyString,
  /** Preview hostname (`pr-<n>-<project>`), allocated once and kept. */
  devHostname: Schema.NullOr(TrimmedNonEmptyString),
  devPort: Schema.NullOr(NonNegativeInt),
  /** See {@link prPreviewSnapshotDataSource}. Null until the data is in place. */
  dataSource: Schema.NullOr(TrimmedNonEmptyString),
  /**
   * Files `git merge` could not reconcile, in `conflict`. Null in every other
   * state.
   *
   * Kept on the row rather than only reported to the caller because the agent
   * that asked is gone by the time a human looks at the card, and "which files"
   * is the only question a conflict raises.
   *
   * Null rather than an empty array, and optional rather than required, for the
   * reason `activeTrial` is: the column is NULL for every row that never
   * conflicted, including every row a pre-063 writer would have left, and the
   * projection decodes the column straight through — an empty array would be a
   * value the database never holds and that every reader would have to map back.
   */
  conflicts: Schema.optional(Schema.NullOr(Schema.Array(TrimmedNonEmptyString))).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  /** Why the build failed, in `failed`. Null in every other state. */
  failureReason: Schema.NullOr(TrimmedNonEmptyString),
  /** The human it was created for. Null when auth is off. */
  createdBy: Schema.NullOr(UserId),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  /** When the pull request was last re-fetched onto this preview. */
  refreshedAt: Schema.NullOr(IsoDateTime),
  /**
   * When the sweep will destroy it. Null while the preview has never reached
   * `ready`: a conflict holds nothing, so there is nothing to expire.
   */
  expiresAt: Schema.NullOr(IsoDateTime),
  /** Set once destroyed. A destroyed preview frees the pull request's slot. */
  destroyedAt: Schema.NullOr(IsoDateTime),
});
export type PrPreview = typeof PrPreview.Type;

/** True while the preview still holds resources and occupies its PR's slot. */
export const isPrPreviewLive = (preview: PrPreview): boolean => preview.destroyedAt === null;

/**
 * True when the preview is serving something.
 *
 * Distinct from "live": a `conflict` occupies the slot for the pull request —
 * asking twice must not build twice — but has no URL to hand anybody, and
 * counting it against `maxAlive` would let three failed merges block a project
 * that is using no disk at all.
 */
export const isPrPreviewAlive = (preview: PrPreview): boolean =>
  preview.destroyedAt === null && (preview.status === "ready" || preview.status === "provisioning");
